/**
 * Redirect de tráfico pagado desde /encuentratucoliving o /findyourcoliving
 * hacia la página de ciudad correspondiente (Opción C).
 *
 * Solo aplica a visitas con UTMs/fbclid/gclid de campañas de pago.
 * El tráfico orgánico y directo sigue viendo la página root.
 */

const ES_CITY_SLUGS = [
  'madrid', 'barcelona', 'valencia', 'malaga', 'sevilla', 'salamanca',
  'granada', 'bilbao', 'alicante', 'zaragoza', 'las-palmas', 'tenerife',
];

const EN_CITY_SLUGS = [
  'madrid', 'barcelona', 'valencia', 'sevilla', 'malaga',
  'new-york', 'manhattan', 'brooklyn', 'jersey-city', 'london',
  'wembley', 'brixton', 'shoreditch', 'canary-wharf', 'tenerife',
];

const CITY_ALIASES = {
  'malaga': ['malaga', 'costa del sol'],
  'tenerife': ['santa cruz', 'tenerife sur', 'santa cruz de tenerife'],
  'las-palmas': ['las palmas', 'gran canaria'],
  'new-york': ['new york', 'newyork', 'nyc'],
  'jersey-city': ['jersey city'],
  'elephant-and-castle': ['elephant and castle', 'elephant & castle'],
};

function getStoredUtm() {
  try {
    const raw = localStorage.getItem('coolliving_utm');
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function extractUtmFromQuery(query) {
  if (!query) return null;
  const utm = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid']
    .forEach((key) => {
      const value = query[key];
      if (value) utm[key] = String(value);
    });
  return Object.keys(utm).length ? utm : null;
}

/** UTMs de la URL actual o recientes en localStorage (≤30 min) */
function getUtmForRedirect(to) {
  const fromQuery = extractUtmFromQuery(to.query);
  if (fromQuery && isPaidTraffic(fromQuery)) return fromQuery;

  const stored = getStoredUtm();
  if (!stored || !isPaidTraffic(stored)) return null;

  const capturedAt = stored._captured_at ? new Date(stored._captured_at).getTime() : 0;
  if (!capturedAt || Date.now() - capturedAt > 30 * 60 * 1000) return null;

  return stored;
}

function isPaidTraffic(utm) {
  if (!utm) return false;
  return !!(
    utm.fbclid ||
    utm.gclid ||
    utm.utm_medium === 'paid_social' ||
    utm.utm_medium === 'cpc' ||
    utm.utm_medium === 'ppc' ||
    utm.utm_source === 'meta' ||
    utm.utm_source === 'facebook' ||
    (utm.utm_source === 'google' && (utm.utm_medium === 'cpc' || utm.utm_medium === 'ppc'))
  );
}

function normalizeHaystack(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function slugMatchesHaystack(slug, haystack) {
  const aliases = CITY_ALIASES[slug] || [slug.replace(/-/g, ' ')];
  return aliases.some((alias) => haystack.includes(normalizeHaystack(alias)));
}

function extractCitySlugFromUtm(utm, isEnglish) {
  const haystack = normalizeHaystack(
    [utm.utm_campaign, utm.utm_term, utm.utm_content, utm._landing_page]
      .filter(Boolean)
      .join(' ')
  );

  const slugs = isEnglish ? EN_CITY_SLUGS : ES_CITY_SLUGS;
  for (const slug of slugs) {
    if (slugMatchesHaystack(slug, haystack)) return slug;
  }
  return null;
}

/**
 * Devuelve { path, query } si debe redirigir, o null si no.
 */
export function resolvePaidTrafficRedirect(to) {
  const isEsRoot = to.path === '/encuentratucoliving';
  const isEnRoot = to.path === '/findyourcoliving';
  if (!isEsRoot && !isEnRoot) return null;

  const utm = getUtmForRedirect(to);
  if (!utm) return null;

  const base = isEsRoot ? '/encuentratucoliving' : '/findyourcoliving';
  const defaultCity = isEsRoot ? 'madrid' : 'new-york';
  const citySlug = extractCitySlugFromUtm(utm, !isEsRoot) || defaultCity;

  return {
    path: `${base}/${citySlug}`,
    query: to.query,
  };
}
